import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import maplibregl, { type Map as MLMap, type GeoJSONSource, type GeoJSONSourceDiff } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { FeatureCollection, LineString, Point } from 'geojson'
import { useRoadStore, filterRoads } from '../../lib/roads/store'
import { isMapClickSuppressed } from '../../lib/roads/mapClickGate'
import {
  mapInstance,
  hydrateStoredUserLocation,
  getStoredUserLocation,
  autoCenterOnUserLocation,
  deepLinkArrival,
  noteUserMapInteraction,
  INITIAL_ZOOM,
} from '../../lib/roads/userLocation'
import { ratingColor, ratingOpacity, bakeOpacity, brightenColor, UNRATED_COLOR } from '../../lib/roads/colors'
import { Moon, RefreshCw, Sun } from 'lucide-react'
import { bringAttributionToFront, getOverlayTop, subscribeOverlayStack } from '../../lib/roads/overlayStack'
import { getMapTheme, setMapTheme, subscribeMapTheme, type MapTheme } from '../../lib/roads/theme'
import { resolveDetailedGeometry, MAX_GEOMETRY_BATCH } from '../../lib/roads/api'
import { isWebGLAvailable } from '../../lib/roads/webgl'
import MapErrorBoundary from './MapErrorBoundary'
import type { RoadRecord } from '../../lib/roads/types'
import {
  trimActive,
  excludedSegment,
  polylineSlice,
  activeTrimsForRoad,
  allTrimsForRoad,
  complementRanges,
  selectionRanges,
  mergeRanges,
  visitCountOf,
  type Coord,
  type RoadTrim,
  type RouteTrims,
} from '../../lib/roads/trim'

// Carto hosted vector GL styles — Dark Matter (dark) and Positron (light).
// (Carto's keyless RASTER tile endpoint now stamps tiles with "API KEY REQUIRED",
// so we consume the hosted vector styles instead.)
const STYLES = {
  dark: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  light: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
} as const

// Drip-render pacing: newly resolved roads stream onto the map sequentially
// instead of popping in chunks. The stream ticks at a fixed one-road cadence
// (DRIP_FLOOR_MS); when roads resolve faster than that cadence can drain
// them, each tick releases the MINIMUM number of roads that still empties
// the backlog within REBUILD_WINDOW_MS — the time window between arrival
// batches — so drawing always keeps pace with the resolve pool and nothing
// is left to dump in one pop at the end of the load.
const REBUILD_WINDOW_MS = 250
const DRIP_FLOOR_MS = 60

type MapTheme = keyof typeof STYLES

interface RoadProps {
  id: string
  color: string
  // Pre-blended casing color (black baked toward the background when the
  // light-mode selection dim is active) — keeps the casing layer opaque too.
  casingColor: string
  // Pre-blended halo color for the route-highlight glow (baked toward the
  // basemap background so self-overlaps can never brighten).
  haloColor: string
  selected: boolean
  hovered: boolean
  inRoute: boolean
  // In-route road that is NOT the one hovered in the route tray — dimmed by
  // baking toward the background (NOT via layer opacity, which would
  // double-blend and create bright spots at self-overlapping segments).
  routeDimmed: boolean
  unrated: boolean
  // Paint-order key: MapLibre draws features in collection order, so this
  // lets us stack higher-rated roads on top where drawings overlap.
  rating: number
  // Authoritative paint-order key consumed by the layers' line-sort-key.
  // Evaluated per-render from properties, so it works identically for a full
  // setData() and an incremental updateData() — a late-resolving road lands
  // at the correct depth automatically. unrated (-100) sinks below every
  // rated road (0-10); highlighted roads (2000+) always float on top.
  sortKey: number
  // True for the dashed EXCLUDED segment features of a trimmed road — these
  // render in the dedicated rr-road-trimmed layer and never hit-test.
  trimmedExcluded?: boolean
  // Pre-blended color for excluded segments (rating color faded ~40% toward
  // the basemap background), baked per feature so the layer stays opaque.
  excludedColor?: string
  // Unique per-FEATURE key for the incremental updateData diff — excluded
  // segments share properties.id with their ridden parent, so the GeoJSON
  // feature id needs its own namespace (properties.id stays the road id for
  // the layer filters).
  featKey?: string
}

type RoadFC = FeatureCollection<LineString | Point, RoadProps>

// Viewport culling margin: roads within this fraction of the viewport size on
// every side count as "on screen" and get their features (re)computed.
const VIEWPORT_MARGIN = 0.5

// Cached built features for one road + the signature of every input that
// shaped them. A matching signature + geometry identity lets a rebuild reuse
// the features verbatim instead of recomputing colors and copying coordinates.
interface RoadFeatureCacheEntry {
  sig: string
  geomRef: unknown
  features: RoadFC['features']
}

const EMPTY_FC: RoadFC = { type: 'FeatureCollection', features: [] }

// Visible road line width (theme-independent). Shared with the dark-mode
// casing layer, which uses it as line-gap-width (see rr-casing below).
const ROAD_WIDTH: any = [
  'interpolate', ['linear'], ['zoom'],
  8, ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 3.15, 3],
  14, ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 7.35, 7],
]

// Shared layer definitions so the road stack can be re-added after a style swap
// (setStyle() removes every custom source/layer).
function addRoadLayers(map: MLMap, theme: MapTheme) {
  if (map.getSource('rr-roads')) return
  map.addSource('rr-roads', { type: 'geojson', data: EMPTY_FC })

  map.addLayer({
    id: 'rr-casing',
    type: 'line',
    source: 'rr-roads',
    filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'trimmedExcluded'], true]],
    // Round joins: the default miter join gets clipped at tight hairpin
    // angles, which punched gaps in the outline on switchback-heavy roads.
    layout: { 'line-join': 'round', 'line-cap': 'round', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      // Per-feature casing color (black, or background-baked black when the
      // light-mode selection dim is active) — the layer stays opaque so the
      // dim can't double-blend at self-overlapping segments.
      'line-color': ['get', 'casingColor'],
      // Light mode: black casing UNDER the road line (wider than it), so a
      // thin sliver peeks out as the outline. Dark mode: light grey casing
      // drawn ONLY OUTSIDE the road's own width via line-gap-width — nothing
      // grey sits beneath the road line, so the road keeps blending against
      // the dark background exactly as designed. Zero extra layers/data.
      'line-gap-width': theme === 'dark' ? ROAD_WIDTH : 0,
      // Selected/hovered roads get a ~5% wider casing + line so the road
      // reads as "thicker" and lifts off the surrounding network. In dark
      // mode ROAD_WIDTH (the gap) already carries the selected/hovered bump,
      // and line-gap-width splits line-width EVENLY on both sides of the
      // gap — so a constant 1.5 here yields a 0.75px rail per side, the
      // same sliver light mode produces ((casing - road) / 2 at both zooms).
      'line-width': theme === 'dark' ? 1.5 : [
              'interpolate', ['linear'], ['zoom'],
              8,
              ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 4.7, 4.5],
              14,
              ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 8.9, 8.5],
            ],
      // No casing under unrated roads — an opaque black underlay defeats
      // the fade meant to set them back. On the light basemap the black
      // casing doubles as a thin outline that separates roads from the pale
      // background, so it stays on in BOTH themes. The light-mode selection
      // dim is baked into casingColor instead of using opacity here, for the
      // same no-double-blend reason as the road color itself.
      'line-opacity': ['case', ['get', 'unrated'], 0, 1],
    },
  })
  map.addLayer({
    id: 'rr-line',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['geometry-type'], 'LineString'],
    // Round joins match the casing: miter joins get clipped at sharp angles,
    // which notched the colored line and exposed the black casing beneath.
    layout: { 'line-join': 'round', 'line-cap': 'round', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      'line-color': ['get', 'color'],
      // ~5% wider when selected/hovered — matches the casing bump above.
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        8, ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 3.15, 3],
        14, ['case', ['any', ['get', 'selected'], ['get', 'hovered']], 7.35, 7],
      ],
      'line-opacity': 1,
    },
  })
  // Dashed dimmed rendering for the EXCLUDED portions of trimmed roads. One
  // extra layer; features carry a pre-blended excludedColor so this stays
  // opaque (no double-blend at overlaps, same rule as the main road line).
  map.addLayer({
    id: 'rr-road-trimmed',
    type: 'line',
    source: 'rr-roads',
    filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'trimmedExcluded'], true]],
    layout: { 'line-join': 'round', 'line-cap': 'round', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      'line-color': ['get', 'excludedColor'],
      'line-dasharray': [1.5, 1.5],
      // Same width as the normal road line; in dark mode the width comes
      // through line-gap-width on the casing layer instead, so mirror the
      // visible-width interpolation here without a black underlay.
      'line-width': ROAD_WIDTH,
    },
  })
  // Invisible click/hover target: 1.5x the visible line width so tight roads
  // are easy to grab. opacity 0 keeps it out of sight but
  // NOTE: trimmedExcluded features are filtered out of hit-testing wherever
  // queryRenderedFeatures results are consumed — excluded stubs must never
  // respond to clicks/hover. still hit-testable.
  // Touch devices get a ~3x wider target (44px-class tap area at typical
  // zooms): fingers are far less precise than a mouse cursor, and taps
  // landing on an adjacent hairpin were selecting the wrong road.
  const coarsePointer =
    typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches
  // Zoomed out, many roads pack the screen — bias the extra width toward low
  // zooms where misses happen most, tapering closer to the visible line up close.
  const hitWidth: unknown = coarsePointer
    ? ['interpolate', ['linear'], ['zoom'], 8, 24, 11, 27, 14, 36]
    : ['interpolate', ['linear'], ['zoom'], 8, 7, 14, 10.5]
  map.addLayer({
    id: 'rr-hit',
    type: 'line',
    source: 'rr-roads',
    // Every line feature is pickable — INCLUDING the dashed EXCLUDED stubs of
    // trimmed roads, so a trimmed-away section stays selectable/hoverable
    // (users tap it to add the road to a route). The stubs keep their dashed,
    // dimmed rendering; only hit-testing changes.
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      'line-color': '#000000',
      'line-width': hitWidth,
      'line-opacity': 0,
    },
  })
  // Halo under the selected road. Wider on touch devices so the selection
  // reads clearly outdoors under bright sun / with gloves on.
  map.addLayer({
    id: 'rr-line-selected-halo',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['get', 'id'], ''],
    paint: {
      'line-color': '#FFFFFF',
      'line-width': coarsePointer
        ? ['interpolate', ['linear'], ['zoom'], 8, 20, 14, 32]
        : ['interpolate', ['linear'], ['zoom'], 8, 10, 14, 18],
      'line-opacity': 0.35,
    },
  })
  map.addLayer({
    id: 'rr-line-selected',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['get', 'id'], ''],
    paint: {
      'line-color': '#FFFFFF',
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 14],
    },
  })
  // Dimmed non-ride roads while a ride is active
  map.addLayer({
    id: 'rr-line-dimmed',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: { visibility: 'none', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 3, 14, 7],
      'line-opacity': 0.12,
    },
  })
  // Route highlight halo — a white glow UNDER each route road so the whole
  // route reads as highlighted while every road keeps its own bike-type color.
  map.addLayer({
    id: 'rr-route-halo',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['get', 'inRoute'], false],
    layout: { 'line-join': 'round', 'line-cap': 'round', visibility: 'none', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      // Pre-blended toward the background per feature — layer stays opaque so
      // self-overlapping segments can't accumulate into bright spots.
      'line-color': ['get', 'haloColor'],
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 9, 14, 16],
    },
  })
  // Route highlight — each road drawn in its OWN color (brightened), just
  // thicker than normal. Hovering a road in the route tray dims the other
  // route roads (but keeps them clearly highlighted) so the hovered one pops.
  map.addLayer({
    id: 'rr-route-line',
    type: 'line',
    source: 'rr-roads',
    filter: ['==', ['get', 'inRoute'], false],
    layout: { 'line-join': 'round', 'line-cap': 'round', visibility: 'none', 'line-sort-key': ['get', 'sortKey'] },
    paint: {
      // Dimming is baked into the feature color (bakeOpacity) — this layer
      // renders fully opaque, so no double-blending at self-overlaps.
      'line-color': ['get', 'color'],
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 10],
    },
  })
  map.addLayer({
    id: 'rr-points',
    type: 'circle',
    source: 'rr-roads',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 14, 8],
      'circle-opacity': 1,
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-width': 1.5,
    },
  })
}

// Remove a range from a merged span — the pieces of `r` that lie OUTSIDE
// `cut` (used to split "sections other visits ride" from the focused visit's).
function subtractRange(r: [number, number], cut: [number, number]): [number, number][] {
  const [s, e] = r
  const [cs, ce] = cut
  if (ce <= s || cs >= e) return [r]
  const out: [number, number][] = []
  if (cs > s) out.push([s, Math.min(cs, e)])
  if (ce < e) out.push([Math.max(ce, s), e])
  return out
}

// Cheap geometry signature (endpoints + midpoint + point count, ~0.1 m
// precision) — replaces JSON.stringify of every coordinate array in the
// updateData diff, which serialized the whole network twice per rebuild.
const fmtCoord = (c: readonly number[]) => `${c[0]?.toFixed(6)},${c[1]?.toFixed(6)}`
function geomSignature(g: LineString | Point): string {
  if (g.type === 'Point') return `P${fmtCoord(g.coordinates)}`
  const c = g.coordinates
  const mid = c[Math.floor(c.length / 2)]
  return `L${c.length}:${fmtCoord(c[0])}:${fmtCoord(mid)}:${fmtCoord(c[c.length - 1])}`
}
// Cheap per-feature property signature — same purpose as geomSignature.
function propsSignature(p: RoadProps): string {
  return (
    `${p.id}|${p.color}|${p.casingColor}|${p.haloColor}` +
    `|${p.selected ? 1 : 0}${p.hovered ? 1 : 0}${p.inRoute ? 1 : 0}${p.routeDimmed ? 1 : 0}${p.unrated ? 1 : 0}` +
    `|${p.rating}|${p.sortKey}|${p.trimmedExcluded ? 1 : 0}|${p.excludedColor ?? ''}|${p.featKey ?? ''}`
  )
}
// Bounding box of a road's drawn coordinates, cached per geometry identity —
// lets viewport culling test a road in O(1) instead of walking its polyline.
function roadBbox(
  id: string,
  geometry: RoadRecord['geometry'] | undefined,
  detailed: Map<string, [number, number][]>,
  cache: Map<string, { ref: unknown; bbox: [number, number, number, number] | null }>
): [number, number, number, number] | null {
  const isPoint = geometry?.type === 'Point'
  const coords = (isPoint ? null : detailed.get(id)) ?? geometry?.coordinates
  if (!coords || !Array.isArray(coords) || (coords as unknown[]).length === 0) return null
  const cached = cache.get(id)
  if (cached && cached.ref === (coords as unknown)) return cached.bbox
  let w = 180
  let e = -180
  let s = 90
  let n = -90
  if (isPoint) {
    const c = coords as unknown as [number, number]
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      w = e = c[0]
      s = n = c[1]
    }
  } else {
    for (const c of coords as unknown as [number, number][]) {
      if (c[0] < w) w = c[0]
      if (c[0] > e) e = c[0]
      if (c[1] < s) s = c[1]
      if (c[1] > n) n = c[1]
    }
  }
  const bbox: [number, number, number, number] = [w, s, e, n]
  cache.set(id, { ref: coords as unknown, bbox })
  return bbox
}

function buildFC(
  roads: RoadRecord[],
  detailed: Map<string, [number, number][]>,
  selectedId: string | null,
  hoveredId: string | null,
  rideIds: Set<string>,
  rideActive: boolean,
  theme: 'dark' | 'light',
  // Visit key of the route-tray chip hovered ("id" or "id#2") — per-visit so
  // duplicate visits of the same road dim/highlight individually.
  routeHoverId: string | null = null,
  recalculatingId: string | null = null,
  recalcWatchId: string | null = null,
  routeTrims: RouteTrims = {},
  detailedGetter: ((id: string) => [number, number][] | undefined) | null = null,
  routeIdList: string[] = [],
  trimFocusKey: string | null = null,
  trimDragLive: { roadId: string; headPos: number | null; tailPos: number | null } | null = null,
  selectedVisitKey: string | null = null,
  featureCache: Map<string, RoadFeatureCacheEntry> | null = null,
  versions: { trims: number; route: number; session: number } = { trims: 0, route: 0, session: 0 }
): RoadFC {
  const features: RoadFC['features'] = []
  // Dark mode mirrors light mode's black casing outline with light grey.
  const outlineColor = theme === 'dark' ? '#4B5563' : '#000000'
  for (const road of roads) {
    if (!road?.geometry?.coordinates || road.geometry.coordinates.length === 0) continue
    // Force Road Recalc cue: the road drops off the map when its recalc
    // starts. Once the panel has the rebuilt record (recalcWatchId set) the
    // road re-enters the FeatureCollection — but ONLY once its fresh geometry
    // has resolved (the detailed.has check below), so the popup that fires on
    // this reappearance always corresponds to a visibly redrawn polyline.
    if (recalculatingId !== null && road.id === recalculatingId && road.id !== recalcWatchId) continue
    const overall = road.ratings?.overall ?? null
    const isPoint = road.geometry.type === 'Point'
    if (!isPoint && !detailed.has(road.id)) continue
    // Hide line roads until their snapped point-map geometry has resolved —
    // no straight-line placeholders between waypoints.
    const resolved = detailedGetter?.(road.id) ?? detailed.get(road.id)
    const coords = resolved ?? road.geometry.coordinates
    if (!isPoint && (!Array.isArray(coords) || coords.length < 2)) continue
    const inRide = rideIds.has(road.id)
    const selected = road.id === selectedId
    const hovered = road.id === hoveredId
    const roadTrims = inRide ? activeTrimsForRoad(routeTrims, road.id) : []
    const visitCount = Math.max(visitCountOf(routeIdList, road.id), roadTrims.length)
    const relTrim =
      (trimFocusKey !== null && trimFocusKey.split('#')[0] === road.id) ||
      (selectedVisitKey !== null && selectedVisitKey.split('#')[0] === road.id) ||
      (trimDragLive !== null && trimDragLive.roadId.split('#')[0] === road.id)
    // Per-road cache signature: every input that can change this road's drawn
    // features. A stable signature (plus unchanged geometry identity) means
    // the cached features are reused verbatim — no color math, no coordinate
    // copying, no re-serialization for the mostly-unchanged majority of roads
    // on every rebuild. Trim-session and hover inputs only count for the
    // roads they can actually affect, so a trim drag or a tray hover rebuilds
    // one/few roads instead of the whole visible set.
    const sig = [
      theme,
      `s${selected ? 1 : 0}`,
      `h${hovered ? 1 : 0}`,
      `r${inRide ? 1 : 0}`,
      inRide ? `w${routeHoverId ?? ''}` : 'w-',
      inRide ? `t${versions.trims}` : 't-',
      inRide ? `v${visitCount}` : 'v-',
      relTrim ? `f${versions.session}` : 'f-',
      `g${versions.route}`,
      `R${rideIds.size === 0 ? 1 : 0}`,
      `L${theme === 'light' && selectedId !== null ? 1 : 0}`,
      `o${overall ?? 'x'}`,
    ].join('|')
    const geomRef: unknown = resolved ?? road.geometry
    const cachedEntry = featureCache?.get(road.id)
    if (cachedEntry && cachedEntry.sig === sig && cachedEntry.geomRef === geomRef) {
      for (const f of cachedEntry.features) features.push(f)
      continue
    }
    const roadFeatures: RoadFC['features'] = []
    const commitRoad = () => {
      featureCache?.set(road.id, { sig, geomRef, features: roadFeatures })
      for (const f of roadFeatures) features.push(f)
    }
    // Trimmed route roads split into TWO features: the ridden portion keeps
    // the normal colored rendering, the excluded portion becomes a dashed,
    // background-faded feature on the rr-road-trimmed layer. Persisted for as
    // long as the route is selected — not just while trim mode is open.
    // A road can appear in the route several times, each visit trimmed
    // differently. Rendering is per-VISIT: an untrimmed visit rides the whole
    // road, a trimmed one only its cut span. A section is dashed ONLY when no
    // visit rides it — so trimming one visit never dashes the full road a
    // second visit rides. Overlapping ridden sections still merge so they
    // never double-draw.
    if (inRide && roadTrims.length > 0) {
      // EVERY visit of the road in the route rides: a visit with no trim
      // record is a full-length pass, so it contributes [0,1]. Only counting
      // visits that HAVE records made a trim on one visit falsely dash the
      // sections another (untrimmed) visit rides.
      // While one visit is in trim mode, that visit's span (live drag values
      // included) is the FOCUS: other visits' sections render faded-solid so
      // the focused span stays obvious; sections NO visit rides stay dashed.
      const baseId = road.id
      // Focus = the visit being trimmed, or the selected visit from the route
      // tray (e.g. viewing a trimmed instance while the other is full-length —
      // the untrimmed one must read as de-emphasized, not at route brightness).
      const trimKey = trimFocusKey && trimFocusKey.split('#')[0] === baseId ? trimFocusKey : null
      const selKey =
        selectedId === baseId &&
        selectedVisitKey &&
        selectedVisitKey.split('#')[0] === baseId
          ? selectedVisitKey
          : null
      const hoverKey = routeHoverId
      // The hovered tray chip is a visit of THIS road when its key is the
      // base id or the base id with a "#N" visit suffix (trimKeyFor keys).
      const hoverOnThisRoad =
        hoverKey !== null && (hoverKey === baseId || hoverKey.startsWith(`${baseId}#`))
      // Hovering one visit acts as a soft focus: that visit's span renders at
      // full route brightness while the road's other visits fade. An active
      // trim session or a selected visit still takes precedence.
      const focusKey = trimKey ?? selKey ?? (hoverOnThisRoad ? hoverKey : null)
      const focusIdx = focusKey
        ? focusKey === baseId
          ? 0
          : Math.max(0, (parseInt(focusKey.split('#')[1] ?? '2', 10) || 2) - 1)
        : -1
      const ranges: [number, number][] = []
      for (let v = 0; v < visitCount; v++) {
        if (v === focusIdx && trimDragLive && trimDragLive.roadId === focusKey) {
          ranges.push([trimDragLive.headPos ?? 0, trimDragLive.tailPos ?? 1])
          continue
        }
        const key = v === 0 ? baseId : `${baseId}#${v + 1}`
        const t = routeTrims[key]
        ranges.push(t && trimActive(t) ? [t.head?.pos ?? 0, t.tail?.pos ?? 1] : [0, 1])
      }
      const allRidden = mergeRanges(ranges)
      // With a visit in focus, THAT visit's span renders as its own feature.
      // Merging everything first (the old behavior) fused a trimmed visit
      // with a full-length one into a single whole-road piece — which can
      // never sit "inside another visit's span", so the fade never matched
      // and nothing dimmed. Focus span and other visits' spans stay separate.
      const focusedSpan = focusIdx >= 0 ? ranges[focusIdx] : null
      // Sections the OTHER visits ride (minus the focused span) — drawn
      // faded-solid below so they read as "still part of the route".
      const otherRidden =
        focusIdx >= 0
          ? mergeRanges(ranges.filter((_, i) => i !== focusIdx)).flatMap((r) =>
              subtractRange(r, focusedSpan as [number, number])
            )
          : []
      const ridden = focusedSpan ? [focusedSpan] : allRidden
      const baseColor = bakeOpacity(
        ratingColor(road.bike_types ?? [], overall),
        ratingOpacity(overall),
        theme
      )
      // Tray-hover dim is per-VISIT: a road can appear in the route several
      // times, each visit trimmed differently — hovering one visit dims only
      // OTHER roads, while this road's other visits fade via the focus span.
      const dimmed = hoverKey !== null && !hoverOnThisRoad
      const baseProps: RoadProps = {
        id: road.id,
        color: dimmed ? bakeOpacity(brightenColor(baseColor), 0.35, theme) : brightenColor(baseColor),
        casingColor: outlineColor,
        haloColor: bakeOpacity('#FFFFFF', dimmed ? 0.08 : 0.3, theme),
        selected: road.id === selectedId,
        hovered: road.id === hoveredId,
        inRoute: true,
        routeDimmed: dimmed,
        unrated: overall === null,
        rating: overall ?? -1,
        sortKey: 2000 + (overall ?? -100),
      }
      // Ridden portion. With a visit in focus: the focused span at full
      // brightness plus each other-visit section as its own faded-solid piece
      // (still part of the route, just not the visit in focus). Without a
      // focus: the merged union, so overlapping passes never double-draw.
      const pieces: { range: [number, number]; faded: boolean }[] = focusedSpan
        ? [
            ...otherRidden.map((r) => ({ range: r, faded: true })),
            { range: focusedSpan, faded: false },
          ]
        : allRidden.map((r) => ({ range: r, faded: false }))
      for (let ri = 0; ri < pieces.length; ri++) {
        const part = polylineSlice(coords, pieces[ri].range[0], pieces[ri].range[1])
        if (part.length < 2) continue
        const fadedSolid = bakeOpacity(baseColor, 0.45, theme)
        const pieceProps = pieces[ri].faded
          ? {
              ...baseProps,
              color: dimmed ? bakeOpacity(fadedSolid, 0.35, theme) : fadedSolid,
              selected: false,
            }
          : baseProps
        roadFeatures.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: part },
          properties: { ...pieceProps, featKey: `${road.id}::ridden-${ri}` },
        })
      }
      // Excluded portion(s), dashed + faded toward the basemap. sortKey stays
      // at the base rating so the ridden line always paints over its stubs.
      const excludedBase = bakeOpacity(baseColor, 0.35, theme)
      // Hover/select on ANY part of the road (kept or dashed) brightens the
      // stubs too — the dashed layer paints from excludedColor only, so the
      // highlight must be baked into that color or the stubs stay dull.
      const exclHighlight = road.id === selectedId || road.id === hoveredId
      let excludedColor = dimmed ? bakeOpacity(excludedBase, 0.35, theme) : excludedBase
      if (exclHighlight) excludedColor = brightenColor(excludedColor)
      const exclProps: RoadProps = {
        ...baseProps,
        color: excludedColor,
        trimmedExcluded: true,
        excludedColor,
        sortKey: overall ?? -100,
      }
      // Excluded stubs come from the UNION of all visits' spans (allRidden),
      // never from the focused span alone — otherwise a focused trimmed visit
      // would dash the sections another visit rides.
      for (const [s, e] of complementRanges(allRidden)) {
        const seg = polylineSlice(coords, s, e)
        if (seg.length >= 2)
          roadFeatures.push({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: seg },
            properties: { ...exclProps, featKey: `${road.id}::excl-${s.toFixed(3)}-${e.toFixed(3)}` },
          })
      }
      commitRoad()
      continue
    }
    // Translucency is baked INTO the color (pre-multiplied over the basemap
    // background) and layers render at opacity 1 — a self-overlapping
    // segment can't brighten because nothing is drawn semi-transparent.
    let color = bakeOpacity(
      ratingColor(road.bike_types ?? [], overall),
      ratingOpacity(overall),
      theme
    )
    // Route roads keep their bike-type color but read brighter than the rest
    // of the network — the highlight IS their color, thicker + a white halo.
    if (inRide) color = brightenColor(color)
    // Selection emphasis: brighten the baked color so the road visually pops.
    if (selected || hovered) color = brightenColor(color)
    // Tray-hover emphasis: dim the OTHER route roads by baking toward the
    // basemap background — identical visual result to lowering opacity, but
    // opaque, so a hairpin overlapping itself can't double-blend into a
    // bright spot. The hovered road keeps its full-brightness color.
    // Per-visit hover key ("id" or "id#2"): treat any "#N" suffix of this
    // road as hovering the road itself in the normal (unsplit) path.
    const dimmed =
      inRide &&
      routeHoverId !== null &&
      !(routeHoverId === road.id || routeHoverId.startsWith(`${road.id}#`))
    if (dimmed) color = bakeOpacity(color, 0.35, theme)
    void selected
    // Selection focus: non-highlighted roads dim to 64% by baking toward the
    // basemap background — visually identical to opacity 0.64 but fully
    // opaque, so a road crossing/overlapping itself can't double-blend into
    // a bright spot (the glitch in the user's screenshot). Light mode only:
    // dark mode keeps every road at full intensity.
    const focusDimmed =
      theme === 'light' &&
      rideIds.size === 0 &&
      selectedId !== null &&
      !selected &&
      road.id !== hoveredId &&
      !inRide
    if (focusDimmed) color = bakeOpacity(color, 0.64, theme)
    const props: RoadProps = {
      id: road.id,
      color,
      // Light mode: black casing doubles as the thin outline (see rr-casing).
      // Dark mode mirrors it with a light grey outline against the dark basemap.
      casingColor: focusDimmed
        ? bakeOpacity(outlineColor, 0.64, theme)
        : outlineColor,
      // Halo baked toward the background at ~30% — never brighter than the
      // road color itself, so overlaps can't produce bright spots either.
      haloColor: bakeOpacity('#FFFFFF', dimmed ? 0.08 : 0.3, theme),
      selected,
      hovered,
      inRoute: inRide,
      routeDimmed: dimmed,
      unrated: overall === null,
      rating: overall ?? -1,
      sortKey: (selected || hovered || inRide ? 2000 : 0) + (overall ?? -100),
    }
    if (isPoint) {
      // Point geometry stores a flat [lng, lat] pair — never a nested array.
      const first = coords[0] as unknown
      const pt: [number, number] = Array.isArray(first)
        ? [first[0], first[1]]
        : [coords[0] as number, coords[1] as number]
      if (typeof pt[0] !== 'number' || typeof pt[1] !== 'number') continue
      roadFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: pt },
        properties: props,
      })
      commitRoad()
      continue
    }
    roadFeatures.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords.map((c) => [c[0], c[1]] as [number, number]),
      },
      properties: props,
    })
    commitRoad()
  }
  void rideActive
  // Paint order no longer depends on collection order: every line layer uses
  // line-sort-key over the per-feature sortKey property, which MapLibre
  // evaluates per-render. That makes the stacking correct even for roads
  // added later via incremental updateData() (a late-resolving unrated road
  // lands UNDER rated roads automatically). This sort is kept as a harmless
  // no-op for consistency.
  const rank = (p: RoadProps) => (p.selected || p.hovered || p.inRoute ? 1 : 0)
  features.sort((a, b) => {
    const ra = rank(a.properties)
    const rb = rank(b.properties)
    if (ra !== rb) return ra - rb
    return a.properties.sortKey - b.properties.sortKey
  })
  return { type: 'FeatureCollection', features }
}

export default function MapCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MLMap | null>(null)
  // Set when WebGL is unavailable (Brave shields, blocked GPU) — swaps the
  // map for a friendly fallback panel instead of a full-page crash.
  const [webglBlocked, setWebglBlocked] = useState(false)
  const hoveredRef = useRef<string | null>(null)
  const detailedRef = useRef<Map<string, [number, number][]>>(new Map())
  // Google Maps link each resolved polyline was derived from (invalidation key).
  const resolvedLinkRef = useRef<Map<string, string | null>>(new Map())
  // Resolved roads waiting to be dripped onto the map one at a time.
  const pendingDripRef = useRef<Map<string, [number, number][]>>(new Map())
  // Roads that must draw the instant their geometry resolves (the selected
  // road) — they skip the reveal stream entirely.
  const priorityRef = useRef<Set<string>>(new Set())
  // Roads currently mid-fetch (a regular worker, or the selected road's own
  // ultra-priority fetch) — lets the ultra lane skip roads already in flight.
  const inFlightRef = useRef<Set<string>>(new Set())
  // Latest enqueueDrip closure, published so the selection handler (which
  // runs BEFORE the drip machinery is defined in each effect pass) can hand
  // the ultra lane's result straight to the drip system.
  const enqueueDripRef = useRef<((id: string, resolved: [number, number][]) => void) | null>(null)
  // Flush-one control published by the drip machinery (null until it exists).
  const dripNowRef = useRef<((id: string) => boolean) | null>(null)
  // Pan/zoom re-ordering of the reveal buffer: comparator + reorder hook
  // published by the queue effect, invoked by the mount-effect move listener.
  const dripCompareRef = useRef<((a: string, b: string) => number) | null>(null)
  const dripReorderRef = useRef<(() => void) | null>(null)
  // Resolved polylines currently sitting in the reveal buffer (needed to
  // rebuild the buffer when it's re-ordered).
  const dripDataByIdRef = useRef<Map<string, [number, number][]>>(new Map())
  const resolveQueueRef = useRef<string[]>([])
  const resolvingRef = useRef(false)
  // Live re-sort hook: the queue effect publishes its current viewport
  // comparator here; the mount-effect 'move' listener calls it on pan/zoom.
  const viewportSortRef = useRef<((force?: boolean) => void) | null>(null)
  // Set the first time the geometry queue is filled — guards the one-shot
  // "map became ready" corrective re-sort against re-hooking on effect re-runs.
  const initialSortHookedRef = useRef(false)

  const roads = useRoadStore((s) => s.roads)
  const filters = useRoadStore((s) => s.filters)
  const setRecalcDrawn = useRoadStore((s) => s.setRecalcDrawn)
  const recalcWatchId = useRoadStore((s) => s.recalcWatchId)
  const selectedId = useRoadStore((s) => s.selectedId)
  const ride = useRoadStore((s) => s.ride)
  const routeIds = useRoadStore((s) => s.routeIds)
  const routeFocusSeq = useRoadStore((s) => s.routeFocusSeq)
  const routeHoverId = useRoadStore((s) => s.routeHoverKey)
  const routeTrims = useRoadStore((s) => s.routeTrims)
  const trimmingRoadId = useRoadStore((s) => s.trimmingRoadId)
  const trimDrag = useRoadStore((s) => s.trimDrag)
  const selectedVisitKey = useRoadStore((s) => s.selectedVisitKey)
  const recalculatingId = useRoadStore((s) => s.recalculatingId)
  const selectRoad = useRoadStore((s) => s.selectRoad)
  const setHoveredId = useRoadStore((s) => s.setHoveredId)

  // The active route = a ride's roads, or (no ride) everything currently in
  // the route tray — highlighted on the map the whole time it's being built.
  const routeHighlightIds = ride ? ride.roadIds : routeIds

  // Theme lives in the shared module so the legend/info bar can follow it.
  const theme = useSyncExternalStore(subscribeMapTheme, getMapTheme)
  const toggleTheme = () => setMapTheme(theme === 'dark' ? 'light' : 'dark')
  const appliedThemeRef = useRef<MapTheme>(theme)

  // filtered roads snapshot for map (kept in a ref to avoid map rebuild churn)
  const filteredRef = useRef<RoadRecord[]>([])
  // Signature of the features currently applied to the GeoJSON source (id →
  // hash of geometry+props). Lets rebuildRef push only CHANGED roads via
  // updateData() instead of setData() on the whole collection — a full
  // setData re-tessellates every road and caused the black flash on the
  // 5-minute background refresh.
  const appliedFeatureRef = useRef<Map<string, string>>(new Map())
  // Geometry-only signature per feature — lets the updateData diff omit
  // newGeometry for property-only changes (the selection dim), so those
  // updates repaint without re-tessellating the road's geometry.
  const appliedGeomRef = useRef<Map<string, string>>(new Map())
  // Paint-order rank per feature (selected/hovered/inRoute → 1). Tracked so a
  // highlight transition can be re-routed through remove+add (updateData
  // replaces in place and can't change draw order).
  const appliedRanksRef = useRef<Map<string, number>>(new Map())
  // True right after the road stack was (re)created — the source is empty and
  // needs a full setData, not an incremental diff.
  const freshSourceRef = useRef(false)
  // Per-road built-feature cache + cheap change detection (see rebuildRef and
  // buildFC): trims get a version counter (fresh object per store update),
  // trim-session/panel state rides object identity, trim drags a serial.
  const featureCacheRef = useRef<Map<string, RoadFeatureCacheEntry>>(new Map())
  const roadBboxRef = useRef<Map<string, { ref: unknown; bbox: [number, number, number, number] | null }>>(new Map())
  const routeTrimsRef = useRef<RouteTrims | null>(null)
  const routeTrimsVersionRef = useRef(0)
  const trimSessionVersionRef = useRef(0)
  const trimDragSerialRef = useRef(0)

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return
    // Brave's strict fingerprinting protection (or a blocked GPU driver) can
    // refuse WebGL entirely — map creation would then throw and blank the
    // whole page. Probe first and show a friendly fallback instead.
    if (!isWebGLAvailable()) {
      setWebglBlocked(true)
      return
    }
    // Load any previously stored location WITHOUT triggering a geolocation
    // request — the prompt is deferred (Onboarding fires it when the intro
    // panels close, or straight away when no intro is shown). The cached fix
    // still shapes the initial center for returning users.
    // Deep-link arrivals skip stored-location centering entirely — the
    // shared road/route link frames the map instead, from the default view.
    if (!deepLinkArrival) hydrateStoredUserLocation()
    // If the device location is already known (cache hit), open centered on
    // the user; the Bay Area view remains the fallback.
    const loc = deepLinkArrival ? null : getStoredUserLocation()
    let map: MLMap
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: STYLES[getMapTheme()],
        center: loc ? [loc.lon, loc.lat] : [-122.2, 37.8],
        zoom: INITIAL_ZOOM,
        attributionControl: false,
      })
    } catch (err) {
      // Covers the race where the probe passes but the constructor still
      // fails to get a context.
      console.error('[MapCanvas] map creation failed:', err)
      setWebglBlocked(true)
      return
    }
    mapRef.current = map
    mapInstance.current = map
    // The location fix often resolves while the intro panels are still open
    // (the geolocation prompt appears before them) — apply it the moment both
    // the fix and the map exist, so the very first visible view is already
    // centered on the user. If it arrives even later, the 'load' hook below
    // still applies it. User pan/zoom cancels auto-centering.
    if (!deepLinkArrival) {
      autoCenterOnUserLocation()
      map.once('load', () => autoCenterOnUserLocation())
    }
    const cancelAutoCenter = () => noteUserMapInteraction()
    map.on('dragstart', cancelAutoCenter)
    map.on('wheel', cancelAutoCenter)
    map.once('remove', () => {
      unsubStack()
      map.off('dragstart', cancelAutoCenter)
      map.off('wheel', cancelAutoCenter)
    })
    map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        // Elevation data source for the road-panel profile charts.
        customAttribution: 'Elevation: Copernicus DEM · Open-Meteo',
      }),
      'bottom-right',
    )
    // compact:true still opens the pill expanded on load (MapLibre collapses it
    // only after the first drag) — mirror the control's own "collapse" toggle
    // state immediately and once the style has loaded.
    const collapseAttrib = () => {
      const el = containerRef.current?.querySelector('.maplibregl-ctrl-attrib')
      if (el) {
        el.setAttribute('open', '')
        el.classList.remove('maplibregl-compact-show')
      }
    }
    collapseAttrib()
    map.once('load', collapseAttrib)
    // The attribution pill and the About bar overlap at the bottom —
    // whichever was touched last renders on top (see overlayStack.ts).
    const syncAttribStacking = () => {
      const onTop = getOverlayTop() === 'attrib'
      // The pill's z-index is trapped inside the bottom-right control
      // container's stacking context — the class must go on the container.
      containerRef.current
        ?.querySelector('.maplibregl-ctrl-bottom-right')
        ?.classList.toggle('rudy-attrib-on-top', onTop)
      containerRef.current
        ?.querySelector('.maplibregl-ctrl-attrib')
        ?.classList.toggle('rudy-on-top', onTop)
    }
    syncAttribStacking()
    const attribEl = containerRef.current?.querySelector('.maplibregl-ctrl-attrib')
    attribEl?.addEventListener('pointerdown', () => {
      bringAttributionToFront()
      syncAttribStacking()
    })
    // The pill's container is promoted when the pill is tapped — without this
    // subscription nothing demotes it when the About bar takes the top slot,
    // so the pill would cover the About bar forever.
    const unsubStack = subscribeOverlayStack(syncAttribStacking)
    map.once('load', syncAttribStacking)
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'bottom-right'
    )
    map.on('error', (e) => {
      const msg = e?.error instanceof Error ? e.error.message : String(e?.error ?? e)
      // Transient basemap tile/style fetch failures (CDN hiccup, brief offline
      // moment) are non-fatal — MapLibre retries tiles automatically.
      if (msg.includes('Failed to fetch')) {
        console.warn('[MapCanvas] basemap tiles briefly unreachable — retrying automatically')
        return
      }
      console.error('[MapCanvas] map error:', e?.error ?? e)
    })

    // Self-healing: after ANY style load (initial load OR theme swap), make
    // sure the road stack exists and is repopulated. setStyle() destroys every
    // custom source/layer, so a robust repair loop is required: poll until the
    // new style is genuinely ready, then re-add layers and rebuild data.
    let repairTimer: number | null = null
    const stopRepair = () => {
      if (repairTimer !== null) {
        window.clearInterval(repairTimer)
        repairTimer = null
      }
    }
    const ensureRoadStack = () => {
      stopRepair()
      addRoadLayers(map, getMapTheme())
      // The source was just recreated — next rebuild must be a full setData.
      freshSourceRef.current = true
      rebuildRef.current?.()
    }
    const startRepair = () => {
      stopRepair()
      repairTimer = window.setInterval(() => {
        try {
          if (map.isStyleLoaded()) ensureRoadStack()
        } catch (err) {
          // map torn down mid-retry — stop the loop
          stopRepair()
          console.error('[MapCanvas] repair loop stopped:', err)
        }
      }, 400)
    }
    let lastMoveSort = 0
    map.on('move', () => {
      // Throttled queue re-sort: as the user pans/zooms, the roads closest to
      // the new map center jump to the front of the loading queue AND to the
      // front of the reveal stream (the drip buffer re-orders too).
      const now = Date.now()
      if (now - lastMoveSort < 500) return
      lastMoveSort = now
      viewportSortRef.current?.()
      dripReorderRef.current?.()
      // Viewport culling is evaluated per rebuild — re-run it (throttled)
      // while the user pans/zooms so the freshly visible area is computed.
      rebuildRef.current?.()
    })
    // Settled camera = the authoritative culling pass: every road that just
    // scrolled into view gets its features computed; culled ones are dropped.
    map.on('moveend', () => rebuildRef.current?.())
    // First-load viewport priority: on phones the queue can fill while the
    // container is still 0x0, so the initial sort runs without usable bounds.
    // Once the map actually loads (and whenever the container resizes, e.g.
    // the mobile browser UI collapsing), force a corrective re-sort — do NOT
    // wait for the user's first pan to fix the ordering.
    map.once('load', () => viewportSortRef.current?.())
    map.on('resize', () => viewportSortRef.current?.())
    map.on('style.load', ensureRoadStack)
    map.on('styledata', () => {
      // If a style swap is in flight, the road stack is gone until the new
      // style settles — keep polling until it's back.
      if (!map.isStyleLoaded() && repairTimer === null) startRepair()
    })
  }, [])

  // Theme toggle → swap style. The persistent styledata listener above re-adds
  // the road stack once the new style is fully loaded.
  useEffect(() => {
    const map = mapRef.current
    if (!map || theme === appliedThemeRef.current) return
    appliedThemeRef.current = theme
    map.setStyle(STYLES[theme])
  }, [theme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const addLayers = () => {
      addRoadLayers(map, getMapTheme())
    }
    if (map.isStyleLoaded()) addLayers()
    else map.once('load', addLayers)
  }, [])

  // Rebuild source data
  const rebuildRef = useRef<(() => void) | null>(null)
  const rebuildRetryRef = useRef<number | null>(null)
  rebuildRef.current = () => {
    const map = mapRef.current
    if (!map) return
    const src = map.getSource('rr-roads') as GeoJSONSource | undefined
    if (!src) return
    // Right after a theme swap the style is not "fully loaded" yet. A dropped
    // rebuild here left the roads blank with no retry — keep one deferred
    // retry pending until the map settles.
    if (!map.isStyleLoaded()) {
      if (rebuildRetryRef.current === null) {
        rebuildRetryRef.current = window.setTimeout(() => {
          rebuildRetryRef.current = null
          rebuildRef.current?.()
        }, 400)
      }
      return
    }
    const rideIds = new Set(routeHighlightIds)
    const hasHighlight = rideIds.size > 0
    // Version stamps for the per-road feature cache (cheap-change detection):
    // routeTrims is a fresh object per store update, so it gets an incrementing
    // version instead of identity. Other inputs ride the object identities.
    if (routeTrimsRef.current !== routeTrims) {
      routeTrimsRef.current = routeTrims
      routeTrimsVersionRef.current += 1
    }
    // Viewport culling: only roads whose drawn extent intersects the map view
    // (plus a margin) are considered for feature computation. Selected,
    // hovered, and route roads are ALWAYS included (panels frame them, hover
    // dimming spans the route), and everything culled now still gets computed
    // when it scrolls back into view. Non-finite bounds (0x0 containers,
    // pre-load) disable culling entirely — the pre-culling behavior.
    let viewport: { w: number; s: number; e: number; n: number } | null = null
    try {
      const b = map.getBounds()
      if (
        Number.isFinite(b.getWest()) && Number.isFinite(b.getEast()) &&
        Number.isFinite(b.getSouth()) && Number.isFinite(b.getNorth())
      ) {
        const latPad = (b.getNorth() - b.getSouth()) * VIEWPORT_MARGIN
        const lonPad = (b.getEast() - b.getWest()) * VIEWPORT_MARGIN
        viewport = {
          w: b.getWest() - lonPad,
          s: Math.max(-89.9, b.getSouth() - latPad),
          e: b.getEast() + lonPad,
          n: Math.min(89.9, b.getNorth() + latPad),
        }
      }
    } catch {
      /* bounds not ready — culling disabled this pass */
    }
    const keepIds = new Set<string>([selectedId, hoveredRef.current, ...routeHighlightIds])
    if (recalcWatchId !== null) keepIds.add(recalcWatchId)
    const culledRoads = viewport
      ? filteredRef.current.filter((r) => {
          if (keepIds.has(r.id)) return true
          const bb = roadBbox(r.id, r.geometry, detailedRef.current, roadBboxRef.current)
          return bb !== null && bb[0] <= viewport.e && bb[2] >= viewport.w && bb[1] <= viewport.n && bb[3] >= viewport.s
        })
      : filteredRef.current
    const fc = buildFC(
      culledRoads,
      detailedRef.current,
      selectedId,
      hoveredRef.current,
      rideIds,
      !!ride,
      getMapTheme(),
      routeHoverId,
      recalculatingId,
      recalcWatchId,
      routeTrims,
      (id) => detailedRef.current.get(id),
      routeIds,
      trimmingRoadId,
      trimDrag,
      selectedVisitKey,
      featureCacheRef.current,
      {
        trims: routeTrimsVersionRef.current,
        route: trimDrag ? trimDragSerialRef.current++ : 0,
        session: trimSessionVersionRef.current,
      }
    )
    // Incremental update: push only features whose geometry/props actually
    // changed via updateData(). A full setData() on the ~1.8k-road collection
    // forces MapLibre to re-tessellate and repaint EVERYTHING, which showed
    // as a black flash on the 5-minute background refresh (and on every
    // hover-driven rebuild). Layer filters/visibility below still update
    // every pass — those are cheap and don't repaint the basemap.
    const rank = (p: RoadProps) => (p.selected || p.hovered || p.inRoute ? 1 : 0)
    const nextSigs = new Map<string, string>()
    const nextGeoms = new Map<string, string>()
    const nextRanks = new Map<string, number>()
    const changed: RoadFC['features'] = []
    const reorder: RoadFC['features'] = []
    for (const f of fc.features) {
      // Excluded trim segments share the road id with their ridden parent —
      // key the diff by the per-feature featKey so parent and stubs diff
      // independently instead of overwriting each other's signatures.
      const key = f.properties.featKey ?? f.properties.id
      const geomSig = geomSignature(f.geometry)
      const sig = propsSignature(f.properties) + geomSig
      nextSigs.set(key, sig)
      nextGeoms.set(key, geomSig)
      nextRanks.set(key, rank(f.properties))
      if (appliedFeatureRef.current.get(key) === sig) continue
      // updateData() replaces features IN PLACE — it can't move a road to the
      // top of the paint order. Highlight transitions (select/hover/route)
      // change the signature AND must re-sort, so route those through
      // remove+add instead (processed remove→update→add: re-added features
      // land at the end = drawn on top).
      const rankChanged = appliedRanksRef.current.get(key) !== rank(f.properties)
      ;(rankChanged ? reorder : changed).push(f)
    }
    // updateData() requires every feature to have a unique id — and the full
    // setData() baseline must be indexed too, or the next diff throws
    // "Cannot update existing geojson data". RoadRecord ids are UUIDs, so
    // stamp every feature unconditionally (excluded trim segments get their
    // namespaced featKey).
    for (const f of fc.features) f.id = f.properties.featKey ?? f.properties.id
    // Full (re)load when the source was just (re)created OR nothing has been
    // applied yet — the mount-path addLayers() doesn't set freshSourceRef,
    // and an updateData() against a not-yet-populated source would silently
    // drop every "update" (ids that don't exist are skipped, not added).
    if (freshSourceRef.current || appliedFeatureRef.current.size === 0) {
      src.setData(fc)
      freshSourceRef.current = false
    } else {
      const removedIds: string[] = []
      for (const id of appliedFeatureRef.current.keys()) {
        if (!nextSigs.has(id)) removedIds.push(id)
      }
      for (const f of reorder) removedIds.push(f.properties.id)
      const diff: GeoJSONSourceDiff<RoadProps> = {}
      if (removedIds.length > 0) diff.remove = removedIds
      // updateData()'s `update` entries must be GeoJSONFeatureDiff objects
      // ({id, newGeometry, addOrUpdateProperties}) — NOT raw features. A raw
      // feature has none of those fields, so applySourceDiff() matched the id
      // and then silently did nothing: same-rank property changes (the
      // selection dim baked into color/casingColor) never reached the map,
      // while rank-changing highlights still worked via the remove+add path.
      if (changed.length > 0) {
        diff.update = changed.map((f) => {
          // Key by featKey — ridden/excluded splits share the road id, so an
          // id-keyed lookup misses (undefined === undefined) and skips the
          // geometry update, leaving a stale full-length solid line on top of
          // the hidden portion (the reverse-then-trim bug).
          const fkey = f.properties.featKey ?? f.properties.id
          const geomChanged = appliedGeomRef.current.get(fkey) !== nextGeoms.get(fkey)
          return {
            id: f.id!,
            ...(geomChanged ? { newGeometry: f.geometry } : {}),
            addOrUpdateProperties: Object.entries(f.properties).map(([key, value]) => ({ key, value })),
          }
        })
      }
      if (reorder.length > 0) diff.add = reorder
      if (diff.remove || diff.update || diff.add) src.updateData(diff)
    }
    appliedFeatureRef.current = nextSigs
    appliedGeomRef.current = nextGeoms
    appliedRanksRef.current = nextRanks
    // Force Road Recalc confirmation: the watched road just re-entered the
    // drawn FeatureCollection (fresh geometry resolved, source updated). Tell
    // the store so the panel shows its success popup only NOW — the road is
    // actually visible on the map, not merely fetched. resetRecalcDrawn is
    // cleared by the panel when it reacts to this signal.
    if (recalcWatchId !== null && fc.features.some((f) => f.properties.id === recalcWatchId)) {
      setRecalcDrawn(recalcWatchId)
    }
    // Route layers must NOT repaint the excluded stubs of trimmed roads —
    // they share the road id, so an id-only filter would draw them at full
    // brightness and defeat the dimmed dashed rendering.
    const rideFilter: unknown = [
      'all',
      ['in', ['get', 'id'], ['literal', Array.from(rideIds)]],
      ['!=', ['get', 'trimmedExcluded'], true],
    ]
    const notRideFilter: unknown = ['!', ['in', ['get', 'id'], ['literal', Array.from(rideIds)]]]
    for (const layer of ['rr-route-line', 'rr-route-halo']) {
      if (map.getLayer(layer)) {
        map.setFilter(layer, hasHighlight ? rideFilter : ['==', ['get', 'id'], ''])
        map.setLayoutProperty(layer, 'visibility', hasHighlight ? 'visible' : 'none')
      }
    }
    if (map.getLayer('rr-line-dimmed')) {
      // Non-route roads dim whenever a route/ride is highlighted.
      map.setLayoutProperty('rr-line-dimmed', 'visibility', hasHighlight ? 'visible' : 'none')
      if (hasHighlight) map.setFilter('rr-line-dimmed', notRideFilter)
    }
    if (map.getLayer('rr-line') && hasHighlight) {
      // Excluded trim stubs skip the normal line layer too (they render in
      // rr-road-trimmed), otherwise the route filter would repaint them.
      map.setFilter('rr-line', [
        'all',
        ['!', ['in', ['get', 'id'], ['literal', Array.from(rideIds)]]],
        ['!=', ['get', 'trimmedExcluded'], true],
      ])
    } else if (map.getLayer('rr-line')) {
      map.setFilter('rr-line', [
        'all',
        ['==', ['geometry-type'], 'LineString'],
        ['!=', ['get', 'trimmedExcluded'], true],
      ])
    }
    // Selection focus (LIGHT MODE ONLY) is baked into each feature's color and
    // casingColor in buildFC() — NO line-opacity expression here. Real
    // transparency double-blends where a road overlaps itself (0.8 alpha drawn
    // twice ≈ 0.96) and brightens the overlap; baked colors stay opaque, so
    // overlaps render exactly like any other segment. Dark mode keeps full
    // intensity everywhere. Rides keep the rr-line-dimmed behavior.
  }

  // filter → rebuild source
  useEffect(() => {
    filteredRef.current = filterRoads(roads, filters)
    // Force Road Recalc: when the flag goes UP, drop the road's resolved
    // polyline so the geometry queue re-derives it from the rebuilt record —
    // it reappears with the NEW geometry, not the stale cached one. Dropping
    // it HERE (the effect that reacts to the flag) guarantees it happens.
    if (recalculatingId !== null) {
      detailedRef.current.delete(recalculatingId)
      resolvedLinkRef.current.delete(recalculatingId)
    }
    if (trimmingRoadId !== null) trimSessionVersionRef.current += 1
    const raf = requestAnimationFrame(() => rebuildRef.current?.())
    return () => cancelAnimationFrame(raf)
  }, [roads, filters, selectedId, ride, routeHighlightIds, routeHoverId, recalculatingId, routeTrims, trimmingRoadId, trimDrag, selectedVisitKey])

  // Geometry resolution queue — concurrent pool + viewport priority +
  // throttled batched rebuilds (replaces the old sequential one-at-a-time
  // pump that made initial map load take minutes).
  useEffect(() => {
    // A resolved polyline is only valid for the Google Maps link it came from.
    // If the sheet changed a road's link, drop its cached geometry so the
    // queue re-derives it (this is what makes live updates appear without a
    // page refresh).
    for (const r of filteredRef.current) {
      if (detailedRef.current.has(r.id) && resolvedLinkRef.current.get(r.id) !== (r.google_maps_url ?? null)) {
        detailedRef.current.delete(r.id)
        resolvedLinkRef.current.delete(r.id)
      }
    }

    // During the "drop off the map" phase (recalc flag up, no watch yet) the
    // recalc road must stay out of the queue. Once the panel confirms the
    // server rebuild (recalcWatchId set) the road is eligible again — the jump
    // below then puts it at the FRONT of the queue. Without this re-entry the
    // road could NEVER be re-enqueued while the flag was up, so its geometry
    // never resolved and the recalc spinner hung forever.
    const hideRecalcRoad = recalculatingId !== null && recalcWatchId === null
    const pending = filteredRef.current.filter(
      (r) =>
        r.id !== (hideRecalcRoad ? recalculatingId : null) &&
        r?.geometry?.type === 'LineString' &&
        (r.geometry.coordinates?.length ?? 0) >= 2 &&
        !detailedRef.current.has(r.id) &&
        !resolveQueueRef.current.includes(r.id)
    )

    // Viewport priority: on-screen roads resolve first, ordered by distance
    // from the map CENTER so the area the user is actually looking at fills
    // in before the edges; off-screen roads follow, also center-ordered.
    const makeViewportCompare = () => {
      const byId = new Map(
        (filteredRef.current.length > 0
          ? filteredRef.current
          : useRoadStore.getState().roads
        ).map((r) => [r.id, r])
      )
      const rank = (id: string): [number, number] => {
        const r = byId.get(id)
        if (!r || r.geometry?.type !== 'LineString' || !r.geometry.coordinates?.length) {
          return [2, Number.MAX_VALUE]
        }
        const c = r.geometry.coordinates
        const px = (c[0][0] + c[c.length - 1][0]) / 2
        const py = (c[0][1] + c[c.length - 1][1]) / 2
        let tier = 2
        let dist = Number.MAX_VALUE
        try {
          const b = mapRef.current!.getBounds()
          const west = b.getWest()
          const east = b.getEast()
          const south = b.getSouth()
          const north = b.getNorth()
          // On mobile the container can still be 0x0 when the queue first
          // fills: getBounds() then returns ±Infinity, EVERY road counts as
          // "in view" and the comparator collapses to a no-op — roads load in
          // sheet order instead of screen-first until the first pan. Treat
          // non-finite bounds as "map not ready" (the load/resize listeners
          // below force a corrective re-sort once real bounds exist).
          if (
            !Number.isFinite(west) || !Number.isFinite(east) ||
            !Number.isFinite(south) || !Number.isFinite(north)
          ) {
            return [2, Number.MAX_VALUE]
          }
          const cx = (west + east) / 2
          const cy = (south + north) / 2
          const dx = px - cx
          const dy = py - cy
          dist = dx * dx + dy * dy
          if (!Number.isFinite(dist)) return [2, Number.MAX_VALUE]
          tier = px >= west && px <= east && py >= south && py <= north ? 0 : 1
        } catch {
          /* bounds not ready yet — priority order is simply skipped */
        }
        return [tier, dist]
      }
      return (x: string, y: string) => {
        const a = rank(x)
        const b = rank(y)
        return a[0] - b[0] || a[1] - b[1]
      }
    }
    // Selection promotion: a road selected from search was usually already
    // enqueued at the BACK of the queue — and the pending filter below skips
    // queued roads, so jumpIds would never include it and it would still wait
    // behind the whole backlog. Pull it out of its queue position and put it
    // at the FRONT so it is the very next road to resolve.
    if (selectedId !== null) {
      const selRoad = filteredRef.current.find((r) => r.id === selectedId) ??
        useRoadStore.getState().roads.find((r) => r.id === selectedId) ?? null
      const selEligible =
        !!selRoad &&
        selRoad.geometry?.type === 'LineString' &&
        !detailedRef.current.has(selectedId) &&
        selectedId !== (hideRecalcRoad ? recalculatingId : null)
    if (selEligible) {
      resolveQueueRef.current = resolveQueueRef.current.filter(
        (id) => id !== selectedId
      )
      // Ultra-priority lane: launch the selected road's geometry fetch in its
      // own dedicated request the moment it is selected, so it never waits for
      // one of the (all-busy) regular workers to finish its in-flight batch.
      // The regular queue keeps its normal priority ordering as a fallback for
      // road-record lookups that fail here.
      if (!inFlightRef.current.has(selectedId)) {
        priorityRef.current.add(selectedId)
        void (async () => {
          inFlightRef.current.add(selectedId)
          try {
            const selRoadNow =
              filteredRef.current.find((r) => r.id === selectedId) ??
              useRoadStore.getState().roads.find((r) => r.id === selectedId) ??
              null
            if (!selRoadNow) {
              // Can't resolve it here — put it back at the front of the
              // regular queue so a worker picks it up.
              resolveQueueRef.current.unshift(selectedId)
              return
            }
            const resolved = await resolveDetailedGeometry(selRoadNow)
            if (resolved) {
              resolvedLinkRef.current.set(selectedId, selRoadNow.google_maps_url ?? null)
              enqueueDripRef.current?.(selectedId, resolved)
            }
          } finally {
            inFlightRef.current.delete(selectedId)
          }
        })()
      }
      dripNowRef.current?.(selectedId)
    }
    }

    // Force Road Recalc: the road being recalculated jumps the ENTIRE queue —
    // it is pushed ahead of viewport-priority ordering and before anything
    // already waiting, so its fresh polyline resolves immediately after the
    // recalc instead of sitting behind every other pending road.
    // The selected road gets the same treatment: clicking a road should draw
    // its detailed polyline right away, not after the background backlog.
    const jumpIds = [
      ...(recalculatingId !== null && pending.some((r) => r.id === recalculatingId)
        ? [recalculatingId]
        : []),
      ...(selectedId !== null && pending.some((r) => r.id === selectedId) ? [selectedId] : []),
    ]
    // Jumped roads must also skip the reveal stream: mark them priority so
    // enqueueDrip draws them the instant their geometry lands, and flush any
    // of them already resolved but still waiting in the buffer.
    priorityRef.current.clear()
    for (const id of jumpIds) priorityRef.current.add(id)
    if (priorityRef.current.size > 0 && dripNowRef.current) {
      for (const id of Array.from(priorityRef.current)) dripNowRef.current(id)
    }
    const rest = pending.filter(
      (r) => r.id !== recalculatingId && r.id !== selectedId
    )
    // makeViewportCompare ranks road IDS; `rest` holds road records, so the
    // comparator must map each record to its id. Sorting the records directly
    // made every rank lookup miss and the comparator returned "equal" for all
    // pairs — the initial queue silently stayed in sheet order, which is why
    // roads used to load scattered across the whole region instead of
    // screen-first until the first pan/zoom re-sort (which passes ids and
    // therefore worked).
    const viewportCompare = makeViewportCompare()
    const ordered = rest.sort((a, b) => viewportCompare(a.id, b.id))
    resolveQueueRef.current.push(...ordered.map((r) => r.id))
    // unshift puts the recalc road at the FRONT; workers already running will
    // still finish their in-flight request first (seconds at most).
    if (jumpIds.length > 0) resolveQueueRef.current.unshift(...jumpIds)

    // Publish the comparator so the (mount-effect) pan/zoom listener can
    // re-sort the remaining queue as the user moves the map — the area they
    // are currently looking at always resolves next (throttled to ~2/sec).
    let lastSortAt = 0
    viewportSortRef.current = (force = false) => {
      const now = Date.now()
      if (!force && now - lastSortAt < 500) return
      lastSortAt = now
      if (resolveQueueRef.current.length > 1) {
        resolveQueueRef.current.sort(makeViewportCompare())
      }
    }

    // First-load corrective re-sort: when the queue fills BEFORE the map has
    // finished initializing (style still loading, mobile container still 0x0),
    // the viewport comparator has no usable bounds yet and the queue stays in
    // sheet order — roads then visibly load "all over the map" instead of
    // screen-first, and only a user pan fixes the order. The moment real
    // bounds become available, force a full re-sort (bypassing the throttle)
    // so on-screen roads resolve first.
    // Runs once per page load, and only counts once it fires against a
    // NON-EMPTY queue with the comparator already published — the effect's
    // first pass can run before the roads arrive (empty queue), and marking
    // the hook done there would skip the real sort forever.
    if (!initialSortHookedRef.current && resolveQueueRef.current.length > 0) {
      const boundsReady = (): boolean => {
        try {
          const b = mapRef.current?.getBounds()
          return (
            !!b &&
            Number.isFinite(b.getWest()) && Number.isFinite(b.getEast()) &&
            Number.isFinite(b.getSouth()) && Number.isFinite(b.getNorth())
          )
        } catch {
          return false
        }
      }
      if (boundsReady()) {
        initialSortHookedRef.current = true
        viewportSortRef.current(true)
      } else {
        let tries = 0
        const initialSortTimer = window.setInterval(() => {
          tries += 1
          const ready = boundsReady()
          if (ready || tries > 40) {
            window.clearInterval(initialSortTimer)
            if (ready) {
              initialSortHookedRef.current = true
              viewportSortRef.current?.(true)
            }
          }
        }, 250)
      }
    }

    if (resolvingRef.current) return
    resolvingRef.current = true

    // Resolved roads are dripped into the drawn set one at a time on an even
    // cadence instead of popping in chunks: the drip rate adapts to the
    // arrival rate (interval = REBUILD_WINDOW_MS / roads waiting to be drawn,
    // clamped to the MAX_DRIP_MS..MIN_DRIP_MS range) so the stream finishes
    // right around when the next batch of resolutions is due. MIN_DRIP_MS is
    // the redraw-rate ceiling that keeps redraw cost bounded on weak devices;
    // rebuildRef dedupes via updateData(), so each drip tick repaints only
    // the one road that just landed.
    let dripTimer: number | null = null
    const dripOne = () => {
      dripTimer = null
      let released = 0
      for (let i = 0; i < dripCount(); i += 1) {
        const next = pendingDripRef.current.entries().next()
        if (next.done) break
        const [id, resolved] = next.value
        pendingDripRef.current.delete(id)
        dripDataByIdRef.current.delete(id)
        detailedRef.current.set(id, resolved)
        released += 1
      }
      // One map repaint per tick — all roads released in the tick land together.
      if (released > 0) rebuildRef.current?.()
      if (pendingDripRef.current.size > 0) {
        dripTimer = window.setTimeout(dripOne, DRIP_FLOOR_MS)
      }
    }
    // Pace the reveal stream: one road per DRIP_FLOOR_MS tick while the
    // backlog is small, but when roads resolve FASTER than that cadence can
    // drain them (big worker pool, all batches landing nearly at once), each
    // tick releases the MINIMUM number of roads that still empties the
    // backlog within REBUILD_WINDOW_MS (the time between arrival batches).
    // Result: the map draws smoothly and sequentially, never falls behind
    // the resolve pool, and never dumps leftovers in one pop at the end.
    const dripCount = () => {
      const backlog = pendingDripRef.current.size
      if (backlog <= 1) return 1
      // Drain the backlog within one window: roads per tick must be at least
      // backlog * tick / window (e.g. 40 waiting → 10 per 60ms tick → 250ms).
      return Math.max(1, Math.ceil((backlog * DRIP_FLOOR_MS) / REBUILD_WINDOW_MS))
    }
    const enqueueDrip = (id: string, resolved: [number, number][]) => {
      // Priority roads (the selected road) skip the stream and draw NOW.
      if (priorityRef.current.has(id)) {
        priorityRef.current.delete(id)
        pendingDripRef.current.delete(id)
        dripDataByIdRef.current.delete(id)
        detailedRef.current.set(id, resolved)
        rebuildRef.current?.()
        return
      }
      pendingDripRef.current.delete(id)
      pendingDripRef.current.set(id, resolved)
      dripDataByIdRef.current.set(id, resolved)
      dripNowRef.current = (pid: string) => {
        const r = pendingDripRef.current.get(pid)
        if (r === undefined) return false
        pendingDripRef.current.delete(pid)
        dripDataByIdRef.current.delete(pid)
        detailedRef.current.set(pid, r)
        rebuildRef.current?.()
        return true
      }
      if (dripTimer === null) dripTimer = window.setTimeout(dripOne, DRIP_FLOOR_MS)
    }
    // Publish for the selection handler: it runs earlier in the effect body,
    // before enqueueDrip exists, and must still be able to hand the ultra
    // lane's fetch result to the drip system on every pass.
    enqueueDripRef.current = enqueueDrip
    // Pan/zoom re-prioritization for the REVEAL stream: the viewport
    // comparator only re-orders roads still waiting to RESOLVE — but with a
    // large worker pool everything resolves nearly at once and waits in the
    // drip buffer instead, which never re-ordered. So the old area kept
    // drawing after a pan while the new area's (already resolved) roads
    // waited behind it. Re-order the drip buffer on map movement too, using
    // the same viewport comparator the resolve queue uses.
    dripCompareRef.current = viewportCompare
    if (dripReorderRef.current === null) {
      dripReorderRef.current = () => {
        if (pendingDripRef.current.size < 2) return
        const compare = dripCompareRef.current
        if (!compare) return
        const ordered = Array.from(pendingDripRef.current.keys()).sort(compare)
        pendingDripRef.current.clear()
        for (const id of ordered) {
          const r = dripDataByIdRef.current.get(id)
          if (r) pendingDripRef.current.set(id, r)
        }
      }
    }

    const CONCURRENCY = MAX_GEOMETRY_BATCH
    const worker = async () => {
      while (resolveQueueRef.current.length > 0) {
        const id = resolveQueueRef.current.shift()!
        // Mark in-flight so the ultra-priority lane never double-fetches a
        // road a regular worker is already resolving.
        inFlightRef.current.add(id)
        try {
          const road = filteredRef.current.find((r: RoadRecord) => r.id === id) ??
            (useRoadStore.getState().roads.find((r: RoadRecord) => r.id === id) ?? null)
          if (!road) continue
          const resolved = await resolveDetailedGeometry(road)
          if (resolved) {
            resolvedLinkRef.current.set(id, road.google_maps_url ?? null)
            enqueueDrip(id, resolved)
          }
        } finally {
          inFlightRef.current.delete(id)
        }
      }
    }
    const runners = Array.from({ length: CONCURRENCY }, () => worker())
    void Promise.allSettled(runners).then(() => {
      resolvingRef.current = false
      // Flush anything still queued in the drip buffer immediately — the
      // resolve pass is over, so there is no "next batch" to pace toward.
      if (dripTimer !== null) window.clearTimeout(dripTimer)
      dripTimer = null
      if (pendingDripRef.current.size > 0) {
        for (const [id, resolved] of pendingDripRef.current) {
          detailedRef.current.set(id, resolved)
        }
        pendingDripRef.current.clear()
      }
      dripDataByIdRef.current.clear()
      dripNowRef.current = null
      rebuildRef.current?.()
    })


    // recalculatingId in deps: when the recalc flag clears, the rebuilt road
    // is missing from detailedRef so this queue re-derives its geometry —
    // that's what makes the road reappear on the map. recalcWatchId: the
    // rebuilt road is enqueued at the FRONT the moment the panel confirms the
    // server rebuild, instead of waiting behind the rest of the backlog.
    // selectedId: clicking a road re-runs the queue so the road jumps to the
    // FRONT (see jumpIds) and its detailed polyline resolves immediately.
  }, [roads, filters, recalculatingId, recalcWatchId, selectedId])

  // Hover + click interaction
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const pickRoad = (e: maplibregl.MapMouseEvent) => {
      // During a theme swap the road layers don't exist yet — querying them
      // throws, so check for the layer before every query.
      if (!map.getLayer('rr-hit')) return []
      return map.queryRenderedFeatures(e.point, { layers: ['rr-hit', 'rr-points'] })
    }
    const onMove = (e: maplibregl.MapMouseEvent) => {
      const feats = pickRoad(e)
      const nextId = feats[0]?.properties?.id ?? null
      if (nextId !== hoveredRef.current) {
        hoveredRef.current = typeof nextId === 'string' ? nextId : null
        setHoveredId(hoveredRef.current)
        requestAnimationFrame(() => rebuildRef.current?.())
      }
      map.getCanvas().style.cursor = nextId ? 'pointer' : ''
    }
    const onClick = (e: maplibregl.MapMouseEvent) => {
      // ANDROID: a tap on the search suggestion list can unmount it
      // mid-touch, so Chrome retargets the leftover synthesized click to the
      // map. Without this gate that click runs selectRoad(null) +
      // dismissSearch() and instantly undoes the pick the user just made.
      if (isMapClickSuppressed()) return
      const id = pickRoad(e)[0]?.properties?.id
      if (typeof id === 'string') selectRoad(id)
      else selectRoad(null)
      // Any map tap also closes the mobile search bar + its results list
      // (UX: "search closes when I tap off it"). The bar itself listens via
      // the counter below. bumpMapClick: other floating panels (account
      // dropdown…) watch the same tap and close themselves.
      useRoadStore.getState().dismissSearch()
      useRoadStore.getState().bumpMapClick()
    }
    map.on('mousemove', onMove)
    map.on('click', onClick)
    return () => {
      map.off('mousemove', onMove)
      map.off('click', onClick)
    }
  }, [selectRoad, setHoveredId])

  // Selected road → fit bounds. Runs ONLY when the selection changes: the
  // background roads refresh must NOT yank the camera back to the road.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !selectedId) return
    const fit = () => {
      const road = useRoadStore.getState().roads.find((r) => r.id === selectedId)
      const coords = detailedRef.current.get(selectedId) ?? road?.geometry?.coordinates
      if (!coords || !Array.isArray(coords) || coords.length < 2) return false
      // When the road has trims in the current route, frame only the ridden
      // portion instead of the whole line. A visit-specific selection (route
      // chip) frames THAT visit's span — full road if untrimmed; a map click
      // frames the union of every visit's ridden span.
      let view = coords as Coord[]
      // A route-chip selection frames that visit's own span; a MAP click
      // (no visit key) frames the ENTIRE road — trimmed or not — so tapping
      // the dashed trimmed-away section still shows the whole line.
      const st = useRoadStore.getState()
      const visitKey = st.selectedVisitKey
      if (visitKey && (visitKey === selectedId || visitKey.startsWith(`${selectedId}#`))) {
        const ranges = selectionRanges(st.routeTrims, st.routeIds, selectedId, visitKey)
        if (ranges) {
          const sliced = ranges.flatMap(([s, e]) => polylineSlice(view, s, e))
          if (sliced.length >= 2) view = sliced
        }
      }
      const lons = view.map((c) => c[0])
      const lats = view.map((c) => c[1])
      const small = window.innerWidth < 768
      // Admin portal docks on the LEFT (anchored ~320px): pad the left side so
      // the framed road clears the admin panel edge when it's open.
      const adminPad =
        document.querySelector('[data-admin-portal="true"]') != null ? 340 : 120
      // Keep the framed road clear of the floating top bar so the upper
      // part of the road never hides behind it.
      const topbar = document.getElementById('rr-topbar')
      const topbarPad = topbar ? Math.ceil(topbar.getBoundingClientRect().height) : 80
      // Mobile: the detail panel is a draggable bottom sheet — measure its
      // ACTUAL height for the bottom pad, but cap it at 50% of the screen so
      // an over-tall sheet doesn't crush the road into the top bar.
      const panel = document.querySelector('[data-road-panel="true"]')
      const panelH = panel instanceof HTMLElement ? panel.offsetHeight : 0
      const panelCap = Math.min(
        panelH > 0 ? panelH : Math.round(window.innerHeight * 0.5),
        Math.round(window.innerHeight * 0.5)
      )
      try {
        map.fitBounds(
          [
            [Math.min(...lons), Math.min(...lats)],
            [Math.max(...lons), Math.max(...lats)],
          ],
          {
            // The road detail panel docks on the RIGHT (~340px wide): pad that
            // side heavily so the camera frames the road in the open map area
            // to the LEFT of the panel — nothing hides behind the popup.
            padding: small
              ? { top: topbarPad + 24, bottom: panelCap + 24, left: 60, right: 60 }
              : { top: topbarPad + 24, bottom: 100, left: adminPad, right: 460 },
            duration: 900,
            maxZoom: 14,
          }
        )
      } catch {
        /* degenerate bounds */
      }
      return true
    }
    // Roads clicked before their snapped point-map geometry has resolved have
    // only coarse sheet waypoints — retry until the detailed line lands so the
    // camera always frames the ENTIRE road, not a partial fallback shape.
    // Mobile: the same tap that selects a road ALSO collapses the mobile
    // search row and dismisses the keyboard, resizing the viewport while the
    // fitBounds animation is in flight — MapLibre aborts the camera move on
    // resize, so the map never travels to the road (desktop unaffected: no
    // keyboard, no layout collapse). Start the camera only after the layout
    // settles, and re-run the fit once if a resize still lands mid-flight.
    const smallView = window.innerWidth < 768
    const runFit = (): boolean => {
      if (!fit()) return false
      if (smallView) {
        const refit = () => fit()
        map.once('resize', refit)
        window.setTimeout(() => map.off('resize', refit), 1600)
      }
      return true
    }
    const started = Date.now()
    const attempt = () => runFit() || Date.now() - started > 4000
    let timer = 0
    if (smallView) {
      // 300ms head start lets the search-row collapse / keyboard-close resize
      // fire BEFORE the camera starts instead of aborting it mid-animation.
      timer = window.setInterval(() => {
        if (attempt()) window.clearInterval(timer)
      }, 300)
    } else {
      if (!attempt()) {
        timer = window.setInterval(() => {
          if (attempt()) window.clearInterval(timer)
        }, 250)
      }
    }
    return () => window.clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  // Ride activation → fit bounds over ride roads
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ride || ride.roadIds.length === 0) return
    const pts: [number, number][] = []
    for (const id of ride.roadIds) {
      const road = roads.find((r) => r.id === id)
      const coords = detailedRef.current.get(id) ?? road?.geometry?.coordinates
      if (Array.isArray(coords)) pts.push(...(coords as [number, number][]))
    }
    if (pts.length === 0) return
    const lons = pts.map((p) => p[0])
    const lats = pts.map((p) => p[1])
    const small = window.innerWidth < 768
    const topbar = document.getElementById('rr-topbar')
    const topbarPad = topbar ? Math.ceil(topbar.getBoundingClientRect().height) : 80
    try {
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        {
          // Same measured top-bar clearance as road selection, so an active
          // ride's upper roads never hide behind the search/filter rows.
          padding: small
            ? { top: topbarPad + 24, bottom: Math.round(window.innerHeight * 0.5) + 24, left: 60, right: 60 }
            : { top: topbarPad + 24, bottom: 100, left: 120, right: 460 },
          duration: 900,
          maxZoom: 13,
        }
      )
    } catch {
      /* ignore */
    }
  }, [ride, roads])

  // "Frame the whole route" — triggered by clicking the Your Route panel's
  // summary (not a road chip). seq increments per click so re-clicks re-frame.
  useEffect(() => {
    if (!routeFocusSeq) return
    const map = mapRef.current
    if (!map) return
    const pts: [number, number][] = []
    for (const id of routeIds) {
      const road = roads.find((r) => r.id === id)
      const coords = detailedRef.current.get(id) ?? road?.geometry?.coordinates
      if (Array.isArray(coords)) pts.push(...(coords as [number, number][]))
    }
    if (pts.length === 0) return
    const lons = pts.map((p) => p[0])
    const lats = pts.map((p) => p[1])
    const topbarRoute = document.getElementById('rr-topbar')
    const topbarPadRoute = topbarRoute
      ? Math.ceil(topbarRoute.getBoundingClientRect().height)
      : 80
    try {
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        {
          // Keep the framed route clear of the right-docked route panel and
          // the measured top bar on both desktop and mobile.
          padding:
            window.innerWidth < 768
              ? { top: topbarPadRoute + 24, bottom: 80, left: 60, right: 60 }
              : { top: topbarPadRoute + 24, bottom: 80, left: 120, right: 480 },
          duration: 900,
          maxZoom: 13,
        }
      )
    } catch {
      /* ignore */
    }
  }, [routeFocusSeq, routeIds, roads])

  const [toggleTop, setToggleTop] = useState(64)

  useEffect(() => {
    const update = () => {
      const container = containerRef.current
      if (!container) return
      const cRect = container.getBoundingClientRect()
      const row2 = document.getElementById('rr-topbar-row2')
      const mobile = row2?.offsetParent != null
      const el = mobile ? row2 : document.getElementById('rr-topbar-row1')
      if (!el) return
      const r = el.getBoundingClientRect()
      // Mobile: sit ON the search row, vertically aligned with it.
      // Desktop: just below the last top-bar row.
      setToggleTop(mobile ? r.top - cRect.top : r.bottom - cRect.top + 8)
    }
    update()
    const ro = new ResizeObserver(update)
    for (const id of ['rr-topbar-row1', 'rr-topbar-row2']) {
      const el = document.getElementById(id)
      if (el) ro.observe(el)
    }
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  return (
    <MapErrorBoundary>
      <div
        ref={containerRef}
        className="absolute inset-0 h-full w-full"
        style={{ background: theme === 'dark' ? '#0A0A0B' : '#E8E6E1' }}
      >
        {webglBlocked && (
          <div className="absolute inset-0 z-40 flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#141416]/95 p-6 text-center shadow-2xl">
              <h2 className="text-lg font-bold tracking-tight text-white">
                The map needs WebGL
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-white/60">
                Your browser is currently blocking the graphics technology the map
                runs on. If you're using Brave, set Shields to "Standard" for this
                site (or allow "Device Recognition"), then reload the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-black hover:bg-amber-300 transition-colors"
              >
                <RefreshCw size={16} />
                Reload page
              </button>
            </div>
          </div>
        )}
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light map' : 'Switch to dark map'}
          aria-label={theme === 'dark' ? 'Switch to light map' : 'Switch to dark map'}
          style={{ top: toggleTop }}
          className="absolute right-3 z-50 h-11 w-11 flex items-center justify-center rounded-lg bg-black/70 backdrop-blur border-2 border-white text-white/90 hover:text-white transition-colors"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>
    </MapErrorBoundary>
  )
}
